/* --------------------------------------------------------------------------
   Tidszon

   Back office sparar kvittona med tid i UTC, och exporten skriver dem utan
   zonangivelse: "2026-03-12T16:19:34" är 17:19 svensk vintertid och 18:19
   sommartid. Utan omräkning hamnar försäljningen en eller två timmar för
   tidigt på dygnet, och ett köp strax efter midnatt hamnar på fel dag —
   på nyårsnatten till och med på fel år.

   Tiden räknas om här, en gång per kvitto, när filen läses in. Allt efter
   det — dag, månad, timme, veckodag — arbetar på den lokala tiden som sträng,
   precis som förut.

   Sommar- och vintertid hämtas ur webbläsarens tidszonsdatabas (Intl) och
   inte ur en egen regel, så att det blir rätt även om EU ändrar reglerna.
   Omräkning FRÅN UTC är alltid entydig: timmen som finns två gånger i
   oktober och timmen som saknas i mars ställer bara till det åt andra hållet.
   -------------------------------------------------------------------------- */

/** Tidszonen kvittona visas i. EasyCashiers kunder finns i Sverige. */
export const TIME_ZONE = 'Europe/Stockholm';

const H = 3600000;

const parts = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit'
});

/* Skillnaden mellan svensk tid och UTC, per UTC-timme. Svensk tid byter
   alltid på hel UTC-timme, så en timmes skillnad gäller hela timmen. En
   export på ett år har några tusen olika timmar — Intl anropas bara för dem. */
const offsetCache = new Map();

/** Millisekunder att lägga till UTC för att få svensk tid vid ett ögonblick. */
export function offsetAt(ms){
  const timme = Math.floor(ms / H);
  let off = offsetCache.get(timme);
  if(off === undefined){
    const p = {};
    for(const x of parts.formatToParts(new Date(timme * H))) p[x.type] = x.value;
    off = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - timme * H;
    offsetCache.set(timme, off);
  }
  return off;
}

const UTAN_ZON  = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;
const MED_ZON   = /(?:Z|[+-]\d{2}:?\d{2})$/;
const BARA_DATUM = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Tidsstämpel från exporten → svensk lokal tid, "ÅÅÅÅ-MM-DDTtt:mm:ss".
 *
 *   utan zon ("2026-03-12T16:19:34")   tolkas som UTC, så som Back office sparar
 *   med zon ("…Z", "…+01:00")          tolkas efter zonen
 *   bara datum ("2026-03-12")          lämnas orört — utan klockslag går det inte att flytta
 *
 * Ger null för det som inte går att tolka.
 */
export function utcToLocal(ts){
  if(typeof ts !== 'string') return null;
  ts = ts.trim();
  if(BARA_DATUM.test(ts)) return ts;

  let ms;
  const m = ts.match(UTAN_ZON);
  if(m){
    const [, y, mo, d, h, mi, s] = m.map(Number);
    if(mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || (s || 0) > 59) return null;
    ms = Date.UTC(y, mo - 1, d, h, mi, s || 0);
  }else if(MED_ZON.test(ts)){
    ms = Date.parse(ts);
  }else{
    return null;
  }
  if(!Number.isFinite(ms)) return null;

  // Ögonblicket flyttat med svensk tids skillnad mot UTC. Dess UTC-fält är
  // då den svenska väggklockan — toISOString läser bara ut dem, den gör ingen
  // egen omräkning till webbläsarens tidszon.
  return new Date(ms + offsetAt(ms)).toISOString().slice(0, 19);
}

/** Dagens datum i svensk tid, oavsett vilken tidszon datorn står i. */
export function todayLocal(){
  return utcToLocal(new Date().toISOString()).slice(0, 10);
}
